import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, XCircle, FileText, AlertCircle } from "lucide-react";
import { Button } from "./Button";
import { useAppDispatch, useAppSelector } from "../../redux";
import { fetchMilestonePaymentLogsByMilestone } from "../../redux/Slices/contractSlice";
import { MilestonePaymentStatusEnum, RoleEnum } from "../../modules";
import { getDynamicCurrencySymbol } from "../../lib/currencyUtils";
import { useTranslation } from "react-i18next";

interface RejectionDetailsDialogProps {
    isOpen: boolean;
    onClose: () => void;
    rejectionData: {
        milestone: any;
        milestoneId: string;
        escrow: any;
    } | null;
    onRequestAgain: (milestone: any) => void;
}

const RejectionDetailsDialog: React.FC<RejectionDetailsDialogProps> = ({
    isOpen,
    onClose,
    rejectionData,
    onRequestAgain
}) => {
    const { t } = useTranslation();
    const [rejectedPaymentLogs, setRejectedPaymentLogs] = useState<any[]>([]);
    const [loadingLogs, setLoadingLogs] = useState(false);
    const [requestingAgain, setRequestingAgain] = useState(false);
    const dispatch = useAppDispatch();
    const { user } = useAppSelector((state) => state.auth);

    // Fetch milestone payment logs and filter rejected ones
    useEffect(() => {
        const fetchRejectedLogs = async () => {
            console.log("Fetching rejected logs for milestone:", rejectionData);
            if (rejectionData?.milestone?.id && isOpen) {
                setLoadingLogs(true);
                try {
                    const result = await dispatch(fetchMilestonePaymentLogsByMilestone(rejectionData.milestone.id)).unwrap();
                    console.log("Fetched milestone payment logs:", result);
                    const logs = result.data || [];

                    // Filter for rejected payment logs and sort by most recent first
                    const rejectedLogs = logs
                        .filter(log => Number(log.paymentStatus?.id) === MilestonePaymentStatusEnum.Rejected)
                        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

                    setRejectedPaymentLogs(rejectedLogs);
                    console.log("Fetched rejected payment logs:", rejectedLogs);
                } catch (error) {
                    console.error("Error fetching milestone payment logs:", error);
                    setRejectedPaymentLogs([]);
                } finally {
                    setLoadingLogs(false);
                }
            }
        };

        fetchRejectedLogs();
    }, [rejectionData?.milestone?.id, dispatch, isOpen]);

    if (!rejectionData) return null;

    const { milestone, milestoneId,escrow } = rejectionData;

    const handleRequestAgain = async () => {
        setRequestingAgain(true);
        try {
            // Pass the appropriate data structure based on what's available
            // If escrow is available (from MessagesAndVideoCall), pass it
            // Otherwise, pass milestone directly (for OrderDetailsModal)
            const dataToPass = escrow || milestone;
            await onRequestAgain(dataToPass);
            onClose();
        } catch (error) {
            console.error("Error requesting payment again:", error);
        } finally {
            setRequestingAgain(false);
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                >
                    <motion.div
                        initial={{ scale: 0.95, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.95, opacity: 0 }}
                        className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
                    >
                        {/* Header */}
                        <div className="sticky top-0 bg-white border-b p-6 flex justify-between items-center">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-red-100 rounded-lg">
                                    <XCircle className="h-6 w-6 text-red-600" />
                                </div>
                                <div>
                                    <h2 className="text-xl font-semibold text-gray-900">{t("rejectionDetailsDialog.paymentRequestRejected")}</h2>
                                    <p className="text-sm text-gray-500">
                                        {t("rejectionDetailsDialog.milestone", { description: milestone.description })}
                                        {rejectedPaymentLogs.length > 1 && ` (${t("rejectionDetailsDialog.rejectionsCount", { count: rejectedPaymentLogs.length })})`}
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={onClose}
                                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>

                        {/* Content */}
                        <div className="p-6 space-y-6">
                            {/* Loading State */}
                            {loadingLogs && (
                                <div className="flex flex-col items-center justify-center py-12">
                                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mb-4"></div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{t("rejectionDetailsDialog.loadingRejectionDetails")}</h3>
                                    <p className="text-gray-600 text-center">{t("rejectionDetailsDialog.loadingMessage")}</p>
                                </div>
                            )}

                            {/* All Rejection Details */}
                            {!loadingLogs && (
                                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                    <div className="flex items-start gap-2 mb-4">
                                        <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
                                        <div>
                                            <h4 className="text-sm font-medium text-red-800">{t("rejectionDetailsDialog.rejectionHistory")}</h4>
                                            <p className="text-xs text-red-600">
                                                {t("rejectionDetailsDialog.rejectionHistoryMessage", { 
                                                    count: rejectedPaymentLogs.length, 
                                                    plural: rejectedPaymentLogs.length > 1 ? "s" : "" 
                                                })}
                                            </p>
                                        </div>
                                    </div>

                                    {/* All Rejection Logs */}
                                    <div className="space-y-4">
                                        {rejectedPaymentLogs.length > 0 ? (
                                            rejectedPaymentLogs.map((paymentLog: any, logIndex: number) => (
                                                <div key={paymentLog.id || logIndex} className="bg-red-100 border border-red-200 rounded-lg p-3">
                                                    <div className="flex items-center justify-between mb-3">
                                                        <p className="text-sm font-medium text-red-800">
                                                            {t("rejectionDetailsDialog.rejectionNumber", { number: logIndex + 1 })}
                                                        </p>
                                                        <p className="text-xs text-red-600">
                                                            {new Date(paymentLog.createdAt).toLocaleString()}
                                                        </p>
                                                    </div>

                                                    {/* Rejection Reason */}
                                                    {paymentLog.reason && (
                                                        <div className="mb-3">
                                                            <p className="text-sm font-medium text-red-800 mb-2">{t("rejectionDetailsDialog.reasonForRejection")}</p>
                                                            <div className="bg-red-200 p-3 rounded-lg">
                                                                <p className="text-sm text-red-700">
                                                                    {paymentLog.reason}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Rejection Attachments */}
                                                    {paymentLog.reasonAttachments && paymentLog.reasonAttachments.length > 0 && (
                                                        <div>
                                                            <p className="text-sm font-medium text-red-800 mb-2">{t("rejectionDetailsDialog.supportingDocuments")}</p>
                                                            <div className="space-y-2">
                                                                {paymentLog.reasonAttachments.map((attachment: any, attachmentIndex: number) => (
                                                                    <div key={attachmentIndex} className="flex items-center justify-between p-2 bg-red-200 rounded">
                                                                        <div className="flex items-center gap-2">
                                                                            <FileText className="h-4 w-4 text-red-600" />
                                                                            <div>
                                                                                <p className="text-sm font-medium text-red-800">
                                                                                    {attachment.name || t("rejectionDetailsDialog.attachment", { number: attachmentIndex + 1 })}
                                                                                </p>
                                                                                <p className="text-xs text-red-600">
                                                                                    {t("rejectionDetailsDialog.supportingDocument")}
                                                                                </p>
                                                                            </div>
                                                                        </div>
                                                                        {attachment.path && (
                                                                            <button
                                                                                onClick={() => window.open(attachment.path, "_blank")}
                                                                                className="px-2 py-1 text-xs text-red-600 hover:text-red-800 hover:bg-red-300 rounded transition-colors"
                                                                            >
                                                                                {t("rejectionDetailsDialog.view")}
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            ))
                                        ) : (
                                            <div className="text-center py-4 text-gray-500">
                                                <p className="text-sm">{t("rejectionDetailsDialog.noRejectionHistory")}</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Milestone Information */}
                            <div className="bg-gray-50 rounded-lg p-4">
                                <h4 className="text-sm font-medium text-gray-900 mb-3">{t("rejectionDetailsDialog.milestoneInformation")}</h4>
                                <div className="space-y-2 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">{t("rejectionDetailsDialog.amount")}</span>
                                        <span className="font-medium">{getDynamicCurrencySymbol()}{milestone.amount}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">{t("rejectionDetailsDialog.description")}</span>
                                        <span className="font-medium text-right max-w-xs">{milestone.description}</span>
                                    </div>
                                    {milestone.dueDate && (
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">{t("rejectionDetailsDialog.dueDate")}</span>
                                            <span className="font-medium">{new Date(milestone.dueDate).toLocaleDateString()}</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex gap-3 pt-4 border-t">
                                {Number(user?.role?.id) != RoleEnum.client && <Button
                                    onClick={handleRequestAgain}
                                    disabled={requestingAgain}
                                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {requestingAgain ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                            {t("rejectionDetailsDialog.processing")}
                                        </>
                                    ) : (
                                        <>
                                            <FileText className="h-4 w-4" />
                                            {t("rejectionDetailsDialog.requestPaymentAgain")}
                                        </>
                                    )}
                                </Button>}
                                <Button
                                    onClick={onClose}
                                    variant="outline"
                                    className="flex-1 border-gray-300 text-gray-700 hover:bg-gray-50"
                                >
                                    {t("rejectionDetailsDialog.close")}
                                </Button>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default RejectionDetailsDialog;



